import posthog from "posthog-js";

type AnalyticsProperties = Record<string, unknown>;

const appName = "tools_for_procreate_web";
const defaultPostHogKey =
  process.env.REACT_APP_POSTHOG_KEY ||
  "phc_BhRF6o6cyHS7Ztw2q4L5iFju8rxxPVBsG9CEdX9C3RFi";
const defaultPostHogHost =
  process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";

let initialized = false;

const isBrowser = () => typeof window !== "undefined";

const cleanProperties = (properties: AnalyticsProperties) =>
  Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined)
  );

const getPageProperties = () => {
  if (!isBrowser()) {
    return {};
  }

  return cleanProperties({
    app: appName,
    path: window.location.pathname,
    search: window.location.search || undefined,
    url: window.location.href,
    referrer: document.referrer || undefined,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
  });
};

const ensureInitialized = () => {
  if (initialized || !isBrowser() || !defaultPostHogKey) {
    return;
  }

  posthog.init(defaultPostHogKey, {
    api_host: defaultPostHogHost,
    autocapture: true,
    capture_pageview: false,
    capture_pageleave: true,
    loaded: (posthogInstance) => {
      const cookieConsent = (window as any).CC;
      if (
        cookieConsent &&
        typeof cookieConsent.allowedCategory === "function" &&
        !cookieConsent.allowedCategory("analytics")
      ) {
        posthogInstance.opt_out_capturing();
      }
    },
  });

  initialized = true;
};

export const analytics = {
  init: () => {
    ensureInitialized();
  },

  identify: (id?: string, properties: AnalyticsProperties = {}) => {
    if (!id) {
      return;
    }

    ensureInitialized();
    posthog.identify(
      id,
      cleanProperties({
        email: id.includes("@") ? id : undefined,
        ...properties,
      })
    );
  },

  /**
   * Ties the funnel's own id to the email the visitor just gave us.
   *
   * The funnel identifies people by a UUID it generates, and logging in
   * identifies them by their email. PostHog will not merge two ids that were
   * each identified outright, so a buyer became two unrelated people: one who
   * paid and vanished, and one who appeared from nowhere and downloaded
   * brushes. Asking "did the people who bought ever open the product" returned
   * zero — not because nobody did, but because the question spans two persons.
   *
   * alias() is the one call that joins ids after the fact. It runs the moment
   * an email is known, so everything before and after the login is one person.
   */
  alias: (email?: string) => {
    if (!email || !email.includes("@")) {
      return;
    }

    ensureInitialized();
    posthog.alias(email.trim().toLowerCase());
  },

  reset: () => {
    ensureInitialized();
    posthog.reset();
  },

  page: (properties: AnalyticsProperties = {}) => {
    ensureInitialized();
    posthog.capture(
      "$pageview",
      cleanProperties({
        ...getPageProperties(),
        ...properties,
      })
    );
  },

  track: (event: string, properties: AnalyticsProperties = {}) => {
    ensureInitialized();
    posthog.capture(
      event,
      cleanProperties({
        ...getPageProperties(),
        ...properties,
      })
    );
  },

  /**
   * Read a feature flag variant. Returns `undefined` until PostHog has loaded
   * flags — callers should wait via `onFeatureFlags` before branching UI on it.
   */
  getFeatureFlag: (key: string): string | boolean | undefined => {
    if (!isBrowser()) {
      return undefined;
    }
    ensureInitialized();
    return posthog.getFeatureFlag(key);
  },

  /**
   * Run `callback` once flags are available, then again on any later change.
   * Returns an unsubscribe function. No-op (returns a noop) outside the browser.
   */
  onFeatureFlags: (callback: () => void): (() => void) => {
    if (!isBrowser()) {
      return () => {};
    }
    ensureInitialized();
    return posthog.onFeatureFlags(callback);
  },

  /**
   * Attach a property to every future event (used to stamp the experiment
   * variant onto the whole funnel so any insight can break down by it).
   */
  register: (properties: AnalyticsProperties) => {
    ensureInitialized();
    posthog.register(cleanProperties(properties));
  },
};
